import axios from 'axios';

import { FieldName as LocationFieldName, Location, getLocations } from 'admin/locations/Locations';
import { Filter, SearchRequest, SortOrder } from 'core/util/Search';
import {
    Asset,
    ScheduledPost,
    CreatePost,
    CreatePosts,
    SchedulePostResponse,
    SchedulePostsResponse,
    LibraryContent,
    MediaUrlValidation,
    Medium,
    TagType,
    UrlMetaData,
    TagMetaDatum,
    KeyPhrase,
    UpdatePost,
    SourceEnum,
    LocationUid,
    CampaignAndLocation,
    PublishContent,
    CampaignMetaDatum,
    SaveContent,
    SaveContentResponse,
    SaveTag,
    SaveTagResponse,
    SourceName,
    SearchMentionResponse,
    MentionData,
    TargetInterest,
    FacebookLocale,
    FacebookGeoLocation,
    EditedDraftPost,
    NewDraftPost,
    LocationTag,
    HashtagData,
    SearchHashtagResponse,
    HashtagSearchType,
    Label,
    LabelMetaDatum,
    SaveLabelsResponse,
    LocationSourceResponse,
    HashtagGroup,
    SearchHashtagGroupResponse,
    SaveHashtagGroupResponse,
    PublishPostType,
    ValidateMentionResponse,
    OptimalPublishTimesResponse,
} from 'social/publisher/Publisher.types';

export const fetchAuthorizedSources = async (
    tenantId: number | undefined,
    search: SearchRequest,
): Promise<LocationSourceResponse> => {
    const { data } = await axios.post<LocationSourceResponse>(
        `/api/tenants/${tenantId}/social2/publishing/authorized-sources`,
        {
            search,
            tenantId,
        },
    );

    return data;
};

export const fetchNonApiAuthorizedSources = async (
    tenantId: number | undefined,
    search: SearchRequest,
): Promise<LocationSourceResponse> => {
    const { data } = await axios.post<LocationSourceResponse>(
        `/api/tenants/${tenantId}/social2/publishing/authorized-sources`,
        {
            search,
            tenantId,
            credentialFilter: 1, // DISABLED
        },
    );

    return data;
};

export const fetchLocations = async (
    agencyId: number | undefined,
    tenantId: number | undefined,
    filters?: Filter[],
): Promise<Location[]> => {
    const search: SearchRequest = {
        filters: [],
        projections: [
            LocationFieldName.InternalName,
            LocationFieldName.Uid,
            LocationFieldName.Id,
            LocationFieldName.Code,
            LocationFieldName.Web,
        ],
        sorts: [
            {
                name: LocationFieldName.InternalName,
                sortOrder: SortOrder.Asc,
            },
        ],
    };

    if (filters && filters.length) {
        search.filters = filters;
    }

    const data = await getLocations(agencyId!, tenantId!, search);

    return data;
};

export const fetchSingleScheduledPost = async (
    tenantId: number | undefined,
    postId: string,
): Promise<ScheduledPost> => {
    const { data } = await axios.post<ScheduledPost>(`/api/tenants/${tenantId}/social2/publishing/get-single-post`, {
        postId,
        filters: [],
    });

    return data;
};

// similar to fetchSingleScheduledPost but deprecated and will be removed later
export const fetchScheduledPost = async (tenantId: number | undefined, postId: string): Promise<ScheduledPost> => {
    const {
        data: { post },
    } = await axios.post<{ post: ScheduledPost }>(`/api/tenants/${tenantId}/social2/publishing/get-post`, {
        postId,
        filters: [],
    });

    return post;
};

export const fetchLibraryContent = async (tenantId: number | undefined, contentId: string): Promise<LibraryContent> => {
    const { data } = await axios.post<LibraryContent>(
        `/api/tenants/${tenantId}/social2/library2/contents/get-single-content`,
        {
            search: {
                start: 0,
                count: 1,
                filters: [
                    {
                        name: '__scr_content_id__',
                        values: [contentId],
                    },
                ],
            },
        },
    );

    return data;
};

export const fetchCacheMedia = async (
    tenantId: number | undefined,
    urls: string[],
): Promise<{ metadatum: Medium[] }> => {
    const { data } = await axios.post<{ metadatum: Medium[] }>(`/api/tenants/${tenantId}/social2/core/cache-media`, {
        urls,
    });

    return data;
};

export const fetchValidateMediaUrl = async (
    tenantId: number | undefined,
    urls: string[],
    publishPostType: PublishPostType,
): Promise<MediaUrlValidation[]> => {
    const { data } = await axios.post<MediaUrlValidation[]>(
        `/api/tenants/${tenantId}/social2/core/validate-media-urls-by-source`,
        {
            options: {
                verbose: false,
                useCase: 0,
            },
            sources: [
                SourceEnum.FACEBOOK,
                SourceEnum.TWITTER,
                SourceEnum.YOUTUBE,
                SourceEnum.INSTAGRAM,
                SourceEnum.GOOGLE_PLACES,
                SourceEnum.LINKEDIN,
                SourceEnum.TIKTOK,
            ],
            urls,
            publishPostType,
        },
    );

    return data;
};

// to be deprecated, use as back up for now if the core's service has failed
export const fetchUrlMetaData = async (tenantId: number | undefined, link: string): Promise<UrlMetaData[]> => {
    const {
        data: { pageMetaData },
    } = await axios.post<{
        pageMetaData: { title: string; description: string; url: string; acceptedImages: string[] };
    }>(`/api/tenants/${tenantId}/social2/core/url-metadata`, {
        url: link,
    });
    const { title, description, url, acceptedImages } = pageMetaData;

    return [
        {
            title,
            description,
            url,
            images:
                acceptedImages &&
                acceptedImages.map((image) => {
                    return { url: image };
                }),
        },
    ];
};

export const extractLinksMetadata = async (
    tenantId: number | undefined,
    urlRequest: { link: { url: string }; extractorSource: number }[],
): Promise<UrlMetaData[]> => {
    const { data } = await axios.post(`/api/tenants/${tenantId}/link-metadata/extract`, urlRequest);

    return data;
};

export const fetchAssets = async (tenantId: number | undefined, search: SearchRequest): Promise<Asset[]> => {
    const {
        data: { assets },
    } = await axios.post<{ assets: Asset[] }>(`/api/tenants/${tenantId}/social2/assets/display-asset`, {
        search,
    });

    return assets;
};

export const fetchAssetTags = async (tenantId: number | undefined): Promise<LabelMetaDatum[]> => {
    const {
        data: { labelMetadata },
    } = await axios.post<{ labelMetadata: LabelMetaDatum[] }>(
        `/api/tenants/${tenantId}/social2/library2/assets/labels-with-count`,
        {
            search: {
                start: 0,
                count: 10000,
                filters: [
                    {
                        name: '__scr_asset_archived__',
                        values: ['false'],
                    },
                ],
            },
            tagType: TagType.ASSET,
        },
    );

    return labelMetadata;
};

export const fetchContentTags = async (tenantId: number | undefined): Promise<TagMetaDatum[]> => {
    const {
        data: { tagMetaData },
    } = await axios.post<{ tagMetaData: TagMetaDatum[] }>(
        `/api/tenants/${tenantId}/social2/content-library/search-tags`,
        {
            search: {
                filters: [{ name: '__scr_tag_name__', values: [''], operator: 6 }],
                start: 0,
                count: 5,
            },
        },
    );

    return tagMetaData;
};

export const fetchLabels = async (tenantId: number | undefined, searchRequest?: SearchRequest): Promise<Label[]> => {
    const search = searchRequest || {
        filters: [],
        start: 0,
    };

    const {
        data: { labels },
    } = await axios.post<{ labels: Label[] }>(
        `/api/tenants/${tenantId}/social2/library2/contents/search-labels`,
        search,
    );

    return labels;
};

export const postSaveTag = async (tenantId: number | undefined, saveTag: SaveTag): Promise<SaveTagResponse> => {
    const { data } = await axios.post<SaveTagResponse>(
        `/api/tenants/${tenantId}/social2/content-library/save-tag`,
        saveTag,
    );

    return data;
};

export const postSaveLabels = async (
    tenantId: number | undefined,
    labelMetaDatums: LabelMetaDatum[],
): Promise<SaveLabelsResponse> => {
    const { data } = await axios.post<SaveLabelsResponse>(
        `/api/tenants/${tenantId}/social2/library2/contents/save-labels`,
        { labelMetaDatums },
    );

    return data;
};

export const fetchAssetCount = async (tenantId: number | undefined, search: SearchRequest): Promise<number> => {
    const {
        data: { count },
    } = await axios.post<{ count: number }>(`/api/tenants/${tenantId}/social2/library2/assets/get-asset-count`, {
        search,
    });

    return count;
};

// to be deprecated
export const postPublishPost = async (tenantId: number, savePost: CreatePost): Promise<SchedulePostResponse> => {
    const { data } = await axios.post<SchedulePostResponse>(
        `/api/tenants/${tenantId}/social2/publishing/save-post`,
        savePost,
    );

    return data;
};

export const postPublishPosts = async (tenantId: number, savePost: CreatePosts): Promise<SchedulePostsResponse> => {
    const { data } = await axios.post<SchedulePostsResponse>(
        `/api/tenants/${tenantId}/social2/publishing/save-posts`,
        savePost,
    );

    return data;
};

export const postNewDraftPost = async (tenantId: number, newDraftPost: NewDraftPost): Promise<Response> => {
    const { data } = await axios.post<Response>(
        `/api/tenants/${tenantId}/social2/publishing/create-draft-post`,
        newDraftPost,
    );

    return data;
};

export const postEditedDraftPost = async (tenantId: number, editedDraftPost: EditedDraftPost): Promise<Response> => {
    const { data } = await axios.post<Response>(
        `/api/tenants/${tenantId}/social2/publishing/update-draft-post`,
        editedDraftPost,
    );

    return data;
};

export const fetchDraftPost = async (tenantId: number | undefined, postId: string): Promise<ScheduledPost> => {
    const { data } = await axios.post<ScheduledPost>(`/api/tenants/${tenantId}/social2/publishing/get-draft-post`, {
        postId,
    });

    return data;
};

export const fetchKeyPhrases = async (tenantId: number | undefined, sourceText: string): Promise<KeyPhrase[]> => {
    const { data } = await axios.post<KeyPhrase[]>(`/api/tenants/${tenantId}/key-phrases/find`, { sourceText });

    return data;
};

export const postUpdatePost = async (tenantId: number, updatePost: UpdatePost): Promise<SchedulePostResponse> => {
    const { data } = await axios.post<SchedulePostResponse>(
        `/api/tenants/${tenantId}/social2/publishing/update-post`,
        updatePost,
    );

    return data;
};

export const fetchLocationUidsByCampaignId = async (
    tenantId: number | undefined,
    campaignId: string,
): Promise<LocationUid[]> => {
    const {
        data: { campaignAndLocations },
    } = await axios.post<{ campaignAndLocations: CampaignAndLocation[] }>(
        `/api/tenants/${tenantId}/social2/core/search-campaign-and-location`,
        {
            search: {
                filters: [{ name: '__campaignandlocation_campaign_id__', values: [campaignId] }],
            },
        },
    );
    const locationUids = campaignAndLocations.map((el: any) => el.baseDomainMetaDatum.locationUid);

    return locationUids;
};

export const postPublishContent = async (
    tenantId: number,
    publishContent: PublishContent,
): Promise<SchedulePostResponse> => {
    const { data } = await axios.post<SchedulePostResponse>(
        `/api/tenants/${tenantId}/social2/publishing/create-scheduled-content`,
        publishContent,
    );

    return data;
};

export const fetchSingleCampaign = async (
    tenantId: number | undefined,
    campaignId: string,
): Promise<CampaignMetaDatum> => {
    const {
        data: { campaignMetaDatum },
    } = await axios.post<{ campaignMetaDatum: CampaignMetaDatum }>(
        `/api/tenants/${tenantId}/social2/campaigns/get-campaign`,
        {
            campaignId,
        },
    );

    return campaignMetaDatum;
};

export const postSaveContent = async (tenantId: number, saveContent: SaveContent): Promise<SaveContentResponse> => {
    const { data } = await axios.post<SaveContentResponse>(
        `/api/tenants/${tenantId}/social2/content-library/save`,
        saveContent,
    );

    return data;
};

export const fetchShortenUrl = async (tenantId: number | undefined, url: string): Promise<string> => {
    const {
        data: { link },
    } = await axios.post<{ link: { domain: string; id: string } }>(
        `/api/tenants/${tenantId}/social2/publishing/url-shorten`,
        { url },
    );

    return `${link.domain}/${link.id}`;
};

export const fetchMention = async (
    tenantId: number | undefined,
    query: string,
    sources: SourceName[],
): Promise<MentionData> => {
    const { data } = await axios.post<SearchMentionResponse>(
        `/api/tenants/${tenantId}/social2/publishing/search-mention`,
        {
            searchMentionRequest: { query, sources },
        },
    );

    return data;
};

export const validateMention = async (
    tenantId: number | undefined,
    query: string,
    source: string,
): Promise<ValidateMentionResponse> => {
    const { data } = await axios.post<ValidateMentionResponse>(
        `/api/tenants/${tenantId}/social2/publishing/validate-mention`,
        {
            validateMentionRequest: { query, source },
        },
    );

    return data;
};

export const fetchHashtag = async (
    tenantId: number | undefined,
    query: string,
    hashtagSearchType: HashtagSearchType,
): Promise<HashtagData> => {
    const { data } = await axios.post<SearchHashtagResponse>(
        `/api/tenants/${tenantId}/social2/publishing/search-hashtag`,
        {
            searchHashtagRequest: { query, hashtagSearchType },
        },
    );

    return data;
};

export const fetchRelatedHashtag = async (
    tenantId: number | undefined,
    queries: string[],
    hashtagSearchType: HashtagSearchType,
): Promise<HashtagData> => {
    const { data } = await axios.post<SearchHashtagResponse>(
        `/api/tenants/${tenantId}/social2/publishing/search-related-hashtag`,
        {
            searchRelatedHashtagRequest: { queries, hashtagSearchType },
        },
    );

    return data;
};

export const fetchHashtagGroups = async (
    tenantId: number | undefined,
    searchRequest?: SearchRequest,
): Promise<SearchHashtagGroupResponse> => {
    const search = searchRequest || {
        filters: [],
        start: 0,
    };

    const { data } = await axios.post<SearchHashtagGroupResponse>(
        `/api/tenants/${tenantId}/social2/publishing/search-hashtag-group`,
        {
            searchRequest: search,
        },
    );

    return data;
};

export const saveHashtagGroup = async (
    tenantId: number | undefined,
    hashtagGroup: HashtagGroup,
): Promise<SaveHashtagGroupResponse> => {
    const { data } = await axios.post<SaveHashtagGroupResponse>(
        `/api/tenants/${tenantId}/social2/publishing/save-hashtag-group`,
        {
            hashtagGroup,
        },
    );

    return data;
};

export const updateHashtagGroup = async (
    tenantId: number | undefined,
    hashtagGroup: HashtagGroup,
): Promise<Response> => {
    const { data } = await axios.post<Response>(`/api/tenants/${tenantId}/social2/publishing/update-hashtag-group`, {
        hashtagGroup,
    });

    return data;
};

export const deleteHashtagGroup = async (tenantId: number | undefined, hashtagGroupId: string): Promise<Response> => {
    const { data } = await axios.post<Response>(`/api/tenants/${tenantId}/social2/publishing/delete-hashtag-group`, {
        hashtagGroupId,
    });

    return data;
};

export const fetchFacebookTargetInterests = async (tenantId: number | undefined): Promise<TargetInterest[]> => {
    const {
        data: { interests },
    } = await axios.get<{ interests: string }>(
        `/api/tenants/${tenantId}/social2/publishing/audience-targeting/interests`,
    );

    let parsedInterests: TargetInterest[] = [];

    try {
        parsedInterests = JSON.parse(interests);
    } catch {
        throw Error('Not able to parse interests data');
    }

    return parsedInterests;
};

export const fetchFacebookLocales = async (tenantId: number | undefined): Promise<FacebookLocale[]> => {
    const {
        data: { locales },
    } = await axios.get<{ locales: string }>(`/api/tenants/${tenantId}/social2/publishing/audience-targeting/locales`);

    let parsedLocales: FacebookLocale[] = [];

    try {
        parsedLocales = JSON.parse(locales);
    } catch {
        throw Error('Not able to parse locales data');
    }

    return parsedLocales;
};

export const fetchFacebookGeoLocations = async (
    tenantId: number | undefined,
    query: string,
): Promise<FacebookGeoLocation> => {
    const {
        data: { geoLocations },
    } = await axios.post<{ geoLocations: FacebookGeoLocation }>(
        `/api/tenants/${tenantId}/social2/publishing/audience-targeting/search-locations`,
        {
            search: { searchTerm: query, collectionLimit: 20 },
        },
    );

    return geoLocations;
};

export const fetchTwitterLocationTag = async (tenantId: number | undefined, query: string): Promise<LocationTag[]> => {
    const {
        data: { locationTags },
    } = await axios.post<{ locationTags: LocationTag[] }>(
        `/api/tenants/${tenantId}/social2/publishing/location-tagging/search-locations`,
        {
            search: { searchTerm: query, tagLimit: 20 },
        },
    );

    return locationTags;
};

export const fetchOptimalPublishTimes = async (
    tenantId: number | undefined,
    search: SearchRequest,
): Promise<OptimalPublishTimesResponse> => {
    const { data } = await axios.post<OptimalPublishTimesResponse>(
        `/api/tenants/${tenantId}/social2/publishing/get-optimal-publish-times`,
        {
            search,
        },
    );

    return data;
};
